import React, { useCallback, useState } from 'react';
import { FileInput } from 'react-admin';
import { useFormContext } from 'react-hook-form';
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';
import uploadVideoService from '../../utils/uploadVideo';
import countUploading from '../../utils/countUploading';
import FileFieldCustom from '../FileFieldCustom/FileFieldCustom';

// FileInput that uploads to Mux as soon as a file is dropped, then writes
// the resulting { id, originalName } back to form state at `source`. The
// form's submit handler can then send just the id — there is no rawFile
// left in form state to upload a second time.
const VideoFileInput = ({
  source,
  label,
  accept = { 'video/mp4': ['.mp4'] },
  values,
  checkedName,
  checkedResource,
  onUploadingChange,
  validate,
  className,
}) => {
  const { setValue, getValues } = useFormContext();
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);

  const handleDrop = useCallback(
    async (newFiles) => {
      if (!newFiles || newFiles.length === 0) {
        return;
      }
      const file = newFiles[0];

      setUploading(true);
      setProgress(0);
      setError(null);
      onUploadingChange?.(true);

      try {
        const result = await uploadVideoService({ rawFile: file }, (progressEvent) => {
          setProgress(countUploading(progressEvent));
        });

        if (result && result.id) {
          // Replace the rawFile-bearing form value with a clean reference
          // so the form save path has nothing left to upload.
          setValue(
            source,
            { id: result.id, originalName: file.name },
            { shouldDirty: true, shouldValidate: true },
          );
        } else {
          setError('Upload failed. Please try again.');
          setValue(source, getValues(source) ?? null);
        }
      } catch (err) {
        setError(err?.message || 'Upload failed. Please try again.');
      } finally {
        setUploading(false);
        setProgress(0);
        onUploadingChange?.(false);
      }
    },
    [source, setValue, getValues, onUploadingChange],
  );

  return (
    <Box>
      <FileInput
        source={source}
        label={label}
        accept={accept}
        validate={validate}
        className={className}
        options={{ onDrop: handleDrop }}
      >
        <FileFieldCustom
          source={source}
          isSubtitle
          values={values}
          checkedName={checkedName}
          checkedResource={checkedResource}
        />
      </FileInput>
      {uploading && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="body2">Uploading {progress}%</Typography>
          <LinearProgress value={progress} variant="determinate" />
        </Box>
      )}
      {error && (
        <Typography variant="body2" color="error" sx={{ mt: 1 }}>
          {error}
        </Typography>
      )}
    </Box>
  );
};

export default VideoFileInput;
